/**
 * Cross-app JWT cookies (E-Living Lab + Localized App).
 * Keep in sync with business_app/client/src/Assets/platformAuthCookie.js when splitting repos.
 *
 * Env: REACT_APP_PLATFORM_COOKIE_* (see client/.env.example)
 */

let subdomainWarned = false;

function readEnv(key, fallback) {
  if (typeof process !== 'undefined' && process.env && process.env[key] != null && process.env[key] !== '') {
    return process.env[key];
  }
  return fallback;
}

function isLocalOrIpHost(hostname) {
  return (
    hostname === 'localhost' ||
    hostname === '[::1]' ||
    /^\d+\.\d+\.\d+\.\d+$/.test(hostname)
  );
}

function normalizeCookieDomain(domain) {
  if (!domain) return '';
  const trimmed = domain.trim();
  if (!trimmed) return '';
  return trimmed.startsWith('.') ? trimmed : `.${trimmed}`;
}

function deriveParentDomainFromHostname(hostname) {
  if (isLocalOrIpHost(hostname)) return '';
  const parts = hostname.split('.').filter(Boolean);
  if (parts.length < 3) return '';
  return `.${parts.slice(-2).join('.')}`;
}

function resolveCookieDomain() {
  const envDomain = normalizeCookieDomain(readEnv('REACT_APP_PLATFORM_COOKIE_DOMAIN', ''));
  if (envDomain) return envDomain;

  if (typeof window !== 'undefined') {
    const derived = deriveParentDomainFromHostname(window.location.hostname);
    if (derived) return derived;

    const host = window.location.hostname;
    const parts = host.split('.').filter(Boolean);
    if (!isLocalOrIpHost(host) && parts.length >= 3 && !subdomainWarned) {
      subdomainWarned = true;
      console.warn(
        '[SSO] Could not derive a parent cookie domain for',
        host,
        '— set REACT_APP_PLATFORM_COOKIE_DOMAIN (e.g. .sqli-services.com) on both frontends.'
      );
    }
  }
  return '';
}

/** @param {string | null | undefined} token */
export function isTokenExpired(token) {
  if (!token) return true;
  try {
    const base64 = token.split('.')[1];
    if (!base64) return true;
    const payload = JSON.parse(
      atob(base64.replace(/-/g, '+').replace(/_/g, '/'))
    );
    if (!payload.exp) return false;
    return payload.exp < Date.now() / 1000;
  } catch {
    return true;
  }
}

function resolveCookieSecure() {
  const envSecure = readEnv('REACT_APP_PLATFORM_COOKIE_SECURE', '');
  if (envSecure === 'true') return true;
  if (envSecure === 'false') return false;
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') return true;
  return false;
}

export function getPlatformAuthCookieConfig() {
  const domain = resolveCookieDomain();
  const secure = resolveCookieSecure();
  const accessName = readEnv('REACT_APP_PLATFORM_ACCESS_COOKIE', 'cerere_access');
  const refreshName = readEnv('REACT_APP_PLATFORM_REFRESH_COOKIE', 'cerere_refresh');
  const maxAgeHours = parseInt(readEnv('REACT_APP_PLATFORM_COOKIE_MAX_AGE_HOURS', '24'), 10);
  const maxAgeSeconds = (Number.isFinite(maxAgeHours) && maxAgeHours > 0 ? maxAgeHours : 24) * 3600;
  return { domain, secure, accessName, refreshName, maxAgeSeconds, sameSite: 'Lax' };
}

function buildCookieString(name, value, { domain, secure, maxAgeSeconds, sameSite }, expire = false) {
  const parts = [
    `${name}=${expire ? '' : encodeURIComponent(value)}`,
    'Path=/',
    `SameSite=${sameSite}`,
  ];
  if (expire) {
    parts.push('Max-Age=0');
    parts.push('Expires=Thu, 01 Jan 1970 00:00:00 GMT');
  } else {
    parts.push(`Max-Age=${maxAgeSeconds}`);
  }
  if (domain) parts.push(`Domain=${domain}`);
  if (secure) parts.push('Secure');
  return parts.join('; ');
}

function getCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

const PLATFORM_LOGOUT_COOKIE = 'cerere_sso_logout';

function collectCookieClearVariants(config) {
  const domains = new Set(['']);
  if (config.domain) domains.add(config.domain);
  if (typeof window !== 'undefined') {
    const derived = deriveParentDomainFromHostname(window.location.hostname);
    if (derived) domains.add(derived);
  }
  const envDomain = normalizeCookieDomain(readEnv('REACT_APP_PLATFORM_COOKIE_DOMAIN', ''));
  if (envDomain) domains.add(envDomain);

  const secures = new Set([false, true]);
  secures.add(config.secure);

  const variants = [];
  domains.forEach((domain) => {
    secures.forEach((secure) => {
      variants.push({ ...config, domain, secure });
    });
  });
  return variants;
}

function expireCookie(name, config) {
  collectCookieClearVariants(config).forEach((variant) => {
    document.cookie = buildCookieString(name, '', variant, true);
  });
}

function clearHostOnlyPlatformCookies(config) {
  const hostOnly = { ...config, domain: '' };
  document.cookie = buildCookieString(config.accessName, '', hostOnly, true);
  document.cookie = buildCookieString(config.refreshName, '', hostOnly, true);
}

function setPlatformLogoutMarker(config) {
  if (!config.domain) return;
  collectCookieClearVariants(config).forEach((variant) => {
    if (!variant.domain) return;
    document.cookie = buildCookieString(PLATFORM_LOGOUT_COOKIE, '1', variant);
  });
}

function clearPlatformLogoutMarker(config) {
  expireCookie(PLATFORM_LOGOUT_COOKIE, config);
}

export function syncPlatformAuthCookies({ accessToken, refreshToken } = {}) {
  if (typeof document === 'undefined') return;
  const config = getPlatformAuthCookieConfig();
  clearPlatformLogoutMarker(config);
  if (config.domain) {
    clearHostOnlyPlatformCookies(config);
  }
  if (accessToken && !isTokenExpired(accessToken)) {
    document.cookie = buildCookieString(config.accessName, accessToken, config);
  }
  if (refreshToken && !isTokenExpired(refreshToken)) {
    document.cookie = buildCookieString(config.refreshName, refreshToken, config);
  }
}

export function clearPlatformAuthCookies() {
  if (typeof document === 'undefined') return;
  const config = getPlatformAuthCookieConfig();
  expireCookie(config.accessName, config);
  expireCookie(config.refreshName, config);
  setPlatformLogoutMarker(config);
}

export function shouldLogoutFromPlatformSso(hasLocalSession) {
  if (!hasLocalSession || typeof document === 'undefined') return false;
  const config = getPlatformAuthCookieConfig();
  if (!config.domain) return false;
  return !!getCookie(PLATFORM_LOGOUT_COOKIE);
}

export function getValidPlatformTokensFromCookies() {
  const config = getPlatformAuthCookieConfig();
  const accessToken = getCookie(config.accessName);
  const refreshToken = getCookie(config.refreshName);
  return {
    accessToken: accessToken && !isTokenExpired(accessToken) ? accessToken : null,
    refreshToken: refreshToken && !isTokenExpired(refreshToken) ? refreshToken : null,
  };
}

/** @deprecated Prefer getValidPlatformTokensFromCookies */
export function hydrateAuthFromPlatformCookies() {
  return getValidPlatformTokensFromCookies();
}

export function mirrorPlatformAuthCookiesFromStorage({ accessToken, refreshToken } = {}) {
  if (typeof document === 'undefined') return;
  const config = getPlatformAuthCookieConfig();
  const toSync = {};
  if (accessToken && !isTokenExpired(accessToken) && !getCookie(config.accessName)) {
    toSync.accessToken = accessToken;
  }
  if (refreshToken && !isTokenExpired(refreshToken) && !getCookie(config.refreshName)) {
    toSync.refreshToken = refreshToken;
  }
  if (toSync.accessToken || toSync.refreshToken) {
    syncPlatformAuthCookies(toSync);
  }
}
